import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { validate as uuidValidate } from "uuid";

import { DatasetSearchRequest, StatusCode } from "@nens/rana/api-v1";
import { getRanaApiV1 } from "@nens/rana/api-v1";
import { objectValues } from "@nens/shared-utils";

import { queryDirectly } from "./hooks/query-directly";

// Single

export type DatasetQueryParams = Omit<DatasetSearchRequest, "tenantId"> | undefined;

const getDatasetQuery = (datasetId: string | undefined) => {
  return getRanaApiV1().getQueryOptions({
    queryKey: ["dataset", "stat", datasetId],
    queryFn: async () => {
      if (!datasetId) throw Error("never happens");

      return getRanaApiV1().DatasetsApi.datasetRetrieve({
        datasetId,
      });
    },
    enabled: !!datasetId,
  });
};

export const useGetDatasetQuery = () => {
  return getDatasetQuery;
};

export const useDatasetQuery = (datasetId: string | undefined) => {
  return useQuery(getDatasetQuery(datasetId));
};

// LIST

const getDatasetListQuery = (params: DatasetQueryParams) => {
  return getRanaApiV1().getQueryOptions({
    queryKey: ["datasets", params],
    queryFn: async () => {
      if (!params) throw Error("Never happens");

      const { search, ...rest } = params;

      const searchParams: DatasetQueryParams = {
        // Show everything except drafts aka "underDevelopment" as in: https://github.com/nens/rana/issues/2776
        status: objectValues(StatusCode).filter((status) => status !== StatusCode.UnderDevelopment),
        ...rest,
        ...(search && {
          // If search is a valid UUID search exact. (Happens if user enters a lizard uuid)
          search: uuidValidate(search) ? search : `${search}*`,
        }),
      };

      return getRanaApiV1().DatasetsApi.datasetSearch(searchParams);
    },
    enabled: !!params,
  });
};

export const useDatasetListQuery = (params: DatasetQueryParams) => {
  return useQuery(getDatasetListQuery(params));
};

// Single (by lizard id)

const getDatasetByLizardIdQuery = (queryClient: QueryClient, lizardId: string) => {
  return getRanaApiV1().getQueryOptions({
    queryKey: ["dataset", "by-lizard-id", lizardId],
    queryFn: async () => {
      const { error, isError, data } = await queryDirectly(
        queryClient,
        getDatasetListQuery({ resourceIdentifierCode: [lizardId], limit: 1 })
      );

      if (isError) throw error;

      const dataset = data.items[0];
      if (!dataset) throw Error(`Dataset with lizard id ${lizardId} not found`);

      queryClient.setQueryData(getDatasetQuery(dataset.id).queryKey, dataset);

      return dataset;
    },
    enabled: !!lizardId,
  });
};

export const useDatasetByLizardIdQueryDirectly = () => {
  const queryClient = useQueryClient();
  return async (lizardId: string) =>
    await queryDirectly(queryClient, getDatasetByLizardIdQuery(queryClient, lizardId));
};

export type DatasetByLizardIdQueryDirectly = ReturnType<typeof useDatasetByLizardIdQueryDirectly>;
